import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { selectCurrentViewState } from '@common/store/navigation/selectors/viewstate.selectors';
import { DiscoverState, SrcTypes, WireTypes } from '@discover/store/models/discover.models';
import { getIsNodeExpandable, getNodeChildren, getNodeLevel, nodeTransformer } from '@discover/utils/discover-search-result-utils';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { environment } from '@src/environments/environment';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { ViewStateOptions } from '@src/suite/common/data-models/navigation/viewstate-options';
import { SingleViewStateRecord } from '@src/suite/common/data-models/records/single-viewstate-record';
import { ResultGroup } from '@src/suite/common/data-models/search-results/result-group';
import { TreeLikeFlatNode, TreeLikeNode } from '@src/suite/common/data-models/search-results/search-results';
import { APMediaEndpoint } from '@src/suite/common/enums/constants';
import { ViewStateTypesEnum } from '@src/suite/common/enums/viewstate-types';
import { selectGroupSortDefs } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { selectCurrentNodeUuid, selectNodeExpandState } from '@src/suite/common/store/list/list.selectors';
import { getGroupByOptions } from '@src/suite/common/utils/viewstate-utils';

const selectDiscoverState = createFeatureSelector<DiscoverState>('discover');
export const selectWires = createSelector(selectDiscoverState, (state: DiscoverState) => {
    return state.wires;
});

export const selectIsLoading = createSelector(selectDiscoverState, (state: DiscoverState) => {
    return state.isLoading;
});
export const selectIsAttachToTopicListLoading = createSelector(selectDiscoverState, (state: DiscoverState) => state.isAttachToTopicListLoading);

export const selectGroupedSortedWires = createSelector(selectCurrentViewState, selectGroupSortDefs, selectWires, (currentViewState, groupSortDefs, wires) => {
    const groupBy = (currentViewState as SingleViewStateRecord)?.viewStateGrouping?.Value || PSTFieldNames.PSTWirePublished;
    const groupByOrder = (currentViewState as SingleViewStateRecord)?.viewStateGroupSortOrder || 'asc';
    const sortBy = (currentViewState as SingleViewStateRecord)?.viewStateSorting?.Value || PSTFieldNames.PSTWirePublished;

    const sortByOrder = (currentViewState as SingleViewStateRecord)?.viewStateSortOrder || 'asc';
    const wireGroup = new ResultGroup<StorytellingWireRecord>();
    const wireGroupingDef = groupSortDefs?.[groupBy];
    const wireSortingDef = groupSortDefs?.[sortBy];

    for (const wire of wires) {
        wireGroup.addToGroups(wireGroupingDef.valueConvertor(wire), wire);
    }
    wireGroup.sort(wireGroupingDef.groupKeyComparator(groupByOrder), wireSortingDef.recordComparator(sortByOrder));
    return wireGroup;
});

export const selectTreeDataSourceControlPair = createSelector(selectGroupedSortedWires, selectNodeExpandState, (wires, nodesExpandState) => {
    const treeControl: FlatTreeControl<TreeLikeFlatNode<StorytellingWireRecord>, string> = new FlatTreeControl<TreeLikeFlatNode<StorytellingWireRecord>, string>(getNodeLevel, getIsNodeExpandable, {
        trackBy: node => node.id,
    });
    const treeFlattener = new MatTreeFlattener<TreeLikeNode<StorytellingWireRecord>, TreeLikeFlatNode<StorytellingWireRecord>, string>(
        nodeTransformer,
        getNodeLevel,
        getIsNodeExpandable,
        getNodeChildren
    );
    // Populates our flattened data into the tree control
    const treeDataSource = new MatTreeFlatDataSource(treeControl, treeFlattener);
    const treeLikeData: TreeLikeNode<StorytellingWireRecord>[] = wires.keys.map(key => ({
        id: key.value,
        uniqueId: key.value,
        label: key.display,
        children: wires.entries[key.value].map(entry => ({
            id: entry.Guid,
            uniqueId: `${key.value}-${entry.Guid}`,
            children: [],
            object: entry,
        })),
    }));
    treeDataSource.data = treeLikeData;
    treeControl.expandAll();
    treeControl.dataNodes.forEach(node => {
        const state = nodesExpandState[node.id];
        if (state != null) {
            if (state === false) {
                treeControl.collapseDescendants(node);
            } else {
                treeControl.expandDescendants(node);
            }
        }
    });
    return { treeDataSource, treeControl };
});

export const selectTreeDataSource = createSelector(selectTreeDataSourceControlPair, pair => pair.treeDataSource);
export const selectTreeControl = createSelector(selectTreeDataSourceControlPair, pair => pair.treeControl);

export const selectCurrentCardItemHeights = createSelector(selectTreeControl, treeControl => {
    const heights = treeControl.dataNodes
        .map(item => {
            if (treeControl.expansionModel.isSelected(item.id)) {
                switch (item.level) {
                    case 0:
                        return 32;
                    default:
                        return 146;
                }
            }
            return item.level ? 0 : 32;
        })
        .filter(height => height > 0);
    return heights;
});

export const selectCurrentCondesedItemHeights = createSelector(selectTreeControl, treeControl => {
    const heights = treeControl.dataNodes.map(item => {
        switch (item.level) {
            case 0:
                return 32;
            default:
                return 60;
        }
    });
    return heights;
});

export const selectSelectedWire = createSelector(selectTreeControl, selectCurrentNodeUuid, (treecontrol, uuid) => {
    return treecontrol.dataNodes.find(node => node.uniqueId === uuid)?.object;
});

export const selectCurrentImage = createSelector(selectDiscoverState, selectSelectedWire, (state, wire) => {
    if (wire?.WireType === WireTypes.ApWire) {
        if (wire.Properties?.PreviewLink) {
            state.selectedImage = `${environment.discover_api_base_url}/${APMediaEndpoint}/${wire.Guid}/type/${SrcTypes.PreviewLink}`;
        } else if (wire.Properties?.MainLink) {
            state.selectedImage = `${environment.discover_api_base_url}/${APMediaEndpoint}/${wire.Guid}/type/${SrcTypes.MainLink}`;
        } else if (wire.Properties?.ThumbnailLink) {
            state.selectedImage = `${environment.discover_api_base_url}/${APMediaEndpoint}/${wire.Guid}/type/${SrcTypes.ThumbnailLink}`;
        }
    }
    return state.selectedImage;
});

export const selectCurrentViewTopics = createSelector(selectDiscoverState, state => {
    return Array.from(state.topics?.values()) || [];
});

export const selectSelectedImages = createSelector(selectSelectedWire, wire => {
    const images: Array<string> = [];
    if (wire?.Properties?.MediaLinks) {
        images.push(wire.Properties.MediaLinks);
    } else if (wire?.WireType === WireTypes.ApWire) {
        images.push(`${environment.discover_api_base_url}/${APMediaEndpoint}/${wire.Guid}/type/${SrcTypes.ThumbnailLink}`);
    }
    return images;
});

export const selectImagesTotalCount = createSelector(selectSelectedImages, images => {
    return images?.length || 0;
});

export const selectTotalGroupedItemCount = createSelector(selectTreeControl, treeControl => {
    return treeControl.dataNodes.filter(node => node.level > 0)?.length || 0;
});

export const selectCurrentImageIndex = createSelector(selectSelectedImages, selectCurrentImage, (media, currentImage) => {
    let idx = 0;
    media.find(item => {
        idx++;
        return item === currentImage;
    });
    return idx;
});

export const selectCurrentNodeIndex = createSelector(selectTreeControl, selectCurrentNodeUuid, (treeControl, currentNodeUuid) => {
    let idx = 0;
    treeControl.dataNodes.find(item => {
        if (item.level > 0) {
            idx++;
        }
        return item?.uniqueId === currentNodeUuid;
    });
    return idx;
});

export const selectDiscoverGroupByOptions = createSelector(selectI18N, i18n => {
    const ret: ViewStateOptions = getGroupByOptions(ViewStateTypesEnum.WIRESEARCH, null, i18n);
    return ret;
});

export const selectDiscoverSortByOptions = createSelector(selectI18N, i18n => {
    const ret: ViewStateOptions = new ViewStateOptions();
    ret.Default = PSTFieldNames.PSTWirePublished;
    ret.Options = [{ id: PSTFieldNames.PSTWirePublished, title: i18n?.PSTWebSortByPubTimeLabel }];
    return ret;
});

export const selectTileViewGroups = createSelector(selectTreeDataSourceControlPair, wires => wires.treeDataSource.data);

export const selectSavedViewStates = createSelector(selectDiscoverState, state => state.storedViewStates);
export const selectCurrentSelectedViewState = createSelector(selectDiscoverState, state => state.currentSelectedViewState);
